import {ApiInfoResponse, ContentType, HttpMethod, HttpService} from "./HttpService";
import {SaveInitialQuizResultsRequest, Settings, UpdateSettingsRequest, User} from "@shared/models/user/User";
import {getRawIdTokenWithGroups} from "@shared/auth/SharedAuth";

/**
 * Notification types that can be marked as read
 */
export enum NotificationType {
    START_HERE_MODAL = "START_HERE_MODAL",
    CONFIGURATION_SUCCESSFUL = "CONFIGURATION_SUCCESSFUL",
    LEARNING_PLANNER_TUTORIAL = "LEARNING_PLANNER_TUTORIAL",
    CHAT_TUTORIAL = "CHAT_TUTORIAL",
    FLASHCARDS_TUTORIAL = "FLASHCARDS_TUTORIAL",
    DOMINATION_PLAN_TUTORIAL = "DOMINATION_PLAN_TUTORIAL",
    PARENT_ONBOARDING_COMPLETED = "PARENT_ONBOARDING_COMPLETED",
    KED_V2_ANNOUNCEMENT = "KED_V2_ANNOUNCEMENT"
}

/**
 * Service for user-related API calls
 */
export class UserService {

    private static pendingWhoAmI: Promise<User> | null = null;
    private static cachedWhoAmI: { data: User; timestamp: number } | null = null;
    private static WHO_AM_I_CACHE_TTL = 10_000; // 10 seconds

    public static async whoAmI(): Promise<User> {
        const now = Date.now();
        if (this.cachedWhoAmI && (now - this.cachedWhoAmI.timestamp) < this.WHO_AM_I_CACHE_TTL) {
            return this.cachedWhoAmI.data;
        }
        if (this.pendingWhoAmI) {
            return this.pendingWhoAmI;
        }
        this.pendingWhoAmI = this.fetchWhoAmI();
        try {
            const result = await this.pendingWhoAmI;
            this.cachedWhoAmI = { data: result, timestamp: Date.now() };
            return result;
        } finally {
            this.pendingWhoAmI = null;
        }
    }

    public static invalidateWhoAmICache(): void {
        this.cachedWhoAmI = null;
        this.pendingWhoAmI = null;
    }

    private static async fetchWhoAmI(): Promise<User> {
        // Fetch both user data and token/groups in parallel
        const [userData, [idToken, groups]] = await Promise.all([
            HttpService.sendRequest<User>(
                HttpMethod.GET,
                '/api/saas/user/whoami'
            ),
            getRawIdTokenWithGroups()
        ]);

        // Merge groups and idToken into the user object
        return {
            ...userData,
            groups: groups || [],
            idToken: idToken || ''
        };
    }

    public static getSettings(): Promise<Settings> {
        return HttpService.sendRequest<Settings>(
            HttpMethod.GET,
            '/api/saas/user/settings'
        );
    }

    public static updateSettings(request: UpdateSettingsRequest): Promise<ApiInfoResponse> {
        return HttpService.sendRequest<ApiInfoResponse>(
            HttpMethod.PUT,
            '/api/saas/user/settings',
            JSON.stringify(request),
            ContentType.JSON
        );
    }

    /**
     * Saves the initial quiz results
     * @param request Request data containing quiz responses and profile data
     * @returns Promise resolving to a response with success status and message
     */
    public static saveInitialQuizResults(request: SaveInitialQuizResultsRequest): Promise<ApiInfoResponse> {
        return HttpService.sendRequest<ApiInfoResponse>(
            HttpMethod.POST,
            '/api/saas/user/initial_quiz',
            JSON.stringify(request),
            ContentType.JSON
        );
    }

    /**
     * Updates user quiz progress field by field (for simplified flow)
     * @param userId User ID to update
     * @param field Field name to update
     * @param value Value to set
     */
    public static updateQuizProgress(userId: string, field: string, value: any): Promise<ApiInfoResponse> {
        return HttpService.sendRequest<ApiInfoResponse>(
            HttpMethod.POST,
            '/api/saas/user/quiz-progress',
            JSON.stringify({userId, field, value}),
            ContentType.JSON
        );
    }

    /**
     * Marks a notification as read for the user - runs in the background without blocking the UI
     * @param notificationType Type of notification that was read
     */
    public static markNotificationAsRead(notificationType: NotificationType): void {
        // Fire and forget - don't await the response
        HttpService.sendRequest<ApiInfoResponse>(
            HttpMethod.POST,
            '/api/saas/user/notification',
            JSON.stringify({type: notificationType}),
            ContentType.JSON
        ).catch(error => {
            // Silent error handling - log to console but don't block UI or show errors to user
            console.log("Failed to mark notification as read:", error);
        });
    }

    /**
     * Marks bell announcements as read — fire and forget
     * @param announcementIds Array of announcement IDs to mark as read
     */
    public static markAnnouncementsAsRead(announcementIds: string[]): void {
        HttpService.sendRequest<ApiInfoResponse>(
            HttpMethod.POST,
            '/api/saas/user/notification',
            JSON.stringify({announcementIds}),
            ContentType.JSON
        ).catch(error => {
            console.log("Failed to mark announcements as read:", error);
        });
    }

    /**
     * Validates if a child can receive a subscription from a parent
     * @param childEmail Email of the child to validate
     * @param childName Name of the child
     */
    public static validateChildForParent(childEmail: string, childName: string): Promise<{
        success: boolean;
        canProceed: boolean;
        message?: string;
        childUserId?: string | null;
        childExists?: boolean;
    }> {
        return HttpService.sendRequest(
            HttpMethod.POST,
            '/api/saas/parent/validate-child',
            JSON.stringify({childEmail, childName}),
            ContentType.JSON
        );
    }

    /**
     * Updates the user's exercise filter preferences
     * @param filters The exercise filter settings to save
     */
    public static updateExerciseFilters(filters: {
        subject?: string;
        module?: string;
        topic?: string;
        level?: string;
        completionStatus?: 'all' | 'completed' | 'uncompleted';
        difficulties?: string;
        searchQuery?: string;
    }): Promise<ApiInfoResponse> {
        return HttpService.sendRequest<ApiInfoResponse>(
            HttpMethod.POST,
            '/api/user/exercise-filters',
            JSON.stringify({filters}),
            ContentType.JSON
        );
    }

    /**
     * Updates the user's exercise settings (answer mode, level, class)
     * @param settings The exercise settings to save
     */
    public static updateExerciseSettings(settings: {
        answerMode?: 'TYPE' | 'DRAW' | 'UPLOAD';
        currentLevel?: string;
        currentClass?: number;
    }): Promise<ApiInfoResponse> {
        return HttpService.sendRequest<ApiInfoResponse>(
            HttpMethod.PUT,
            '/api/saas/user/settings',
            JSON.stringify({
                type: 'EXERCISE',
                exerciseSettings: settings
            }),
            ContentType.JSON
        );
    }

    /**
     * Gets or generates Discord verification code for the user
     * @returns Promise with verification code or Discord linked status
     */
    public static getDiscordVerificationCode(): Promise<{
        verificationCode?: string;
        discordLinked: boolean;
        discordId?: string;
        newAchievements?: string[];
    }> {
        return HttpService.sendRequest(
            HttpMethod.GET,
            '/api/saas/user/discord-verification-code'
        );
    }
}
